import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Trans, useLingui } from '@lingui/react/macro';

import { theme } from '../styles/GlobalStyle';
import { useWindowWidth } from '../../hooks/useWindowSize';

import { TopOfFold } from '../page-components-copy/top-of-fold';
import { ColorBanner } from '../page-components-copy/color-banner';
import { ImageAndList } from '../page-components-copy/image-and-list';

import {
  Bold,
  ButtonLink,
  Paragraph,
  SectionTitle,
  Spacer,
  UnbiasedResources,
} from '../page-components-copy/components';
import { IconFocusCTA } from '../page-components-copy/icon-focus-cta';
import { ROUTE_PATHS } from '../_pages/_utils/router.utils';
import { useAuthContext } from '../../providers/auth.provider';
import { useLocationContext } from '../../providers/location.provider';
import { IMAGES, getImage, handleTap } from '../../utils';

const person1 = getImage(IMAGES.PERSON_1.ref);
const Square1 = getImage(IMAGES.SQUARE_1.ref);
const Square2 = getImage(IMAGES.SQUARE_2.ref);
const Square3 = getImage(IMAGES.SQUARE_3.ref);
const LaptopWithMug = getImage(IMAGES.LAPTOP_BLUE_MUG.ref);
const BalloonDog = getImage(IMAGES.BALLOON_DOG.ref);
const CoffeeAndPencil = getImage(IMAGES.COFFEE_AND_PENCIL.ref);
const HouseTag = getImage(IMAGES.HOUSE_TAG.ref);
const BetterLogo = getImage(IMAGES.BETTER.ref);
const QuonticLogo = getImage(IMAGES.QUONTIC.ref);
const MarbleLogo = getImage(IMAGES.MARBLE.ref);
const RenoFiLogo = getImage(IMAGES.RENOFI.ref);

const StyledImageAndList = styled(ImageAndList)`
  & > div:last-child {
    @media ${({ theme }) => theme.mq.desktopLargeMin} {
      max-width: 640px !important;
      margin-left: 32px !important;
    }
    & p span:last-child {
      font-size: 21px;
    }
  }
`;

const House = styled('img')`
  display: flex;
  z-index: -1;
  max-height: 227px;
  max-width: 210px;
  align-self: center;
  margin: auto;
  @media ${theme.mq.desktopLargeMin} {
    margin: unset;
    max-height: 495px;
    max-width: 535px;
    margin-right: 20px;
  }
`;

export enum PARTNER {
  better = 'better',
  quontic = 'quontic',
  marble = 'marble',
  renofi = 'renofi',
}

const PARTNER_LOGO = {
  [PARTNER.better]: BetterLogo,
  [PARTNER.quontic]: QuonticLogo,
  [PARTNER.marble]: MarbleLogo,
  [PARTNER.renofi]: RenoFiLogo,
};

const PARTNER_NAME = {
  [PARTNER.better]: 'Better',
  [PARTNER.quontic]: 'Quontic',
  [PARTNER.marble]: 'Marble',
  [PARTNER.renofi]: 'RenoFi',
};

const PARTNER_COLOR = {
  [PARTNER.quontic]: '#F2994A',
};

const Landing: React.FC<{
  partner: PARTNER;
  campaign?: string;
  className?: string;
}> = ({ partner, campaign = '', className }) => {
  const navigate = useNavigate();
  const { handleSignUp } = useAuthContext();
  const { isPuertoRico } = useLocationContext();
  const { isMobile } = useWindowWidth();
  const { t } = useLingui();
  const logo = PARTNER_LOGO[partner];
  if (!campaign) {
    campaign = 'default';
  }

  const onSignUp = () => {
    handleSignUp();
    navigate(ROUTE_PATHS.ONBOARDING);
  };

  const solveDifferenceListItems = [
    {
      icon: null,
      text: (
        <span>
          <Bold>
            {isPuertoRico ? (
              <Trans>Get clear, simple information.</Trans>
            ) : (
              <Trans>Get simple, unbiased advice.</Trans>
            )}
          </Bold>
          <br />
          {isPuertoRico ? (
            <Trans>
              You choose whenever to share your data. We offer a premium service
              without advertising and clear information from a team of PhDs
              to help you understand your options and next steps.
            </Trans>
          ) : (
            <Trans>
              You choose whenever to share your data. We offer a premium service
              without advertising and clear, unbiased information from a team of PhDs
              to help you understand your options and next steps.
            </Trans>
          )}
        </span>
      ),
    },
    {
      icon: null,
      text: (
        <span>
          <Bold><Trans>We take your privacy seriously. (Seriously.)</Trans></Bold>
          <br />
          <Trans>
            Ever given out your phone or email in exchange for a price quote and
            then get bombarded with robocalls and sales messages? Yea, we don’t do
            that.
          </Trans>
        </span>
      ),
    },
    {
      icon: null,
      text: (
        <span>
          <Bold><Trans>We make it fast and easy.</Trans></Bold>
          <br />
          {isPuertoRico ? (
            <Trans>
              Our financial AI helps you compare borrowing options and
              understand your choices.
            </Trans>
          ) : (
            <Trans>
              Our financial AI helps you make better borrowing decisions to save
              money, get more money out, or buy more home.
            </Trans>
          )}
        </span>
      ),
    },
  ];

  const howItWorksItems = [
    {
      icon: <img src={Square1} alt={t`Number 1 in a square`} />,
      text: (
        <span>
          <Bold><Trans>Answer a few questions</Trans></Bold>{' '}
          <Trans>about your current financial situation.</Trans>
        </span>
      ),
    },
    {
      icon: <img src={Square2} alt={t`Number 2 in a square`} />,
      text: (
        <span>
          <Bold>
            {isPuertoRico ? (
              <Trans>The Debt Optimizer works its tech magic</Trans>
            ) : (
              <Trans>The Debt Optimizer works its unbiased, tech magic</Trans>
            )}
          </Bold>{' '}
          <Trans>after getting your credit report and score.</Trans>
        </span>
      ),
    },
    {
      icon: <img src={Square3} alt={t`Number 3 in a square`} />,
      text: (
        <span>
          <Bold><Trans>Evaluate your options</Trans></Bold>{' '}
          {isPuertoRico ? (
            <Trans>and the quickest ways to see your options.</Trans>
          ) : (
            <Trans>and the quickest ways to increase your homebuying power.</Trans>
          )}
        </span>
      ),
    },
  ];

  const PARTNER_HEADERS: Record<string, Record<string, React.ReactNode>> & { DEFAULT: React.ReactNode } = {
    [PARTNER.quontic]: { default: <Trans>Debt is stressful.</Trans> },
    [PARTNER.renofi]: { default: <Trans>Debt is Stressful</Trans> },
    [PARTNER.better]: {
      default: (
        <Trans>
          <b>Unlock that door:</b>
          <br />
          <span>Improve your credit and get approved</span>
        </Trans>
      ),
      cash: (
        <Trans>
          <b>Unlock that loan:</b>
          <br />
          <span>Improve your credit and get approved</span>
        </Trans>
      ),
    },
    DEFAULT: isPuertoRico ? (
      <Trans>Unlock that door: improve your credit and see your options</Trans>
    ) : (
      <Trans>Unlock that door: improve your credit and secure the perfect mortgage</Trans>
    ),
  };

  const PARTNER_COPY: Record<string, Record<string, React.ReactNode>> & { DEFAULT: React.ReactNode } = {
    [PARTNER.quontic]: {
      default: isPuertoRico ? (
        <Trans>
          Is your credit or DTI (debt-to-income) affecting your borrowing options? Our Debt Optimizer gives you clear information about your credit and DTI so you can understand your options. Get your personalized results in minutes.
        </Trans>
      ) : (
        <Trans>
          Has your credit or DTI (debt-to-income) prevented you from a home mortgage? Our Debt Optimizer tech gives you unbiased advice on how to fix your credit and improve your DTI so you can get into the house you deserve. On average, people have increased their home-buying power by nearly 30%. Get your personalized results in minutes.
        </Trans>
      ),
    },
    [PARTNER.better]: {
      default: isPuertoRico ? (
        <Trans>
          Our Debt Optimizer helps you understand your options in minutes. See your credit score and get clear information about your finances so you can decide on your next steps.
        </Trans>
      ) : (
        <Trans>
          Our Debt Optimizer helps you increase your homebuying power in minutes. Improve your credit score and optimize your finances to buy the home of your dreams. On average, Better users experience an increase in home-buying power of nearly 30% and potential savings of over $1,000 per year.
        </Trans>
      ),
      cash: isPuertoRico ? (
        <Trans>
          Our Debt Optimizer helps you explore cash options in minutes. See your credit score and get information about your finances and options, whether it's debt consolidation or a home improvement you've been considering.
        </Trans>
      ) : (
        <Trans>
          Our Debt Optimizer helps you unlock cash in minutes. We help improve your credit score and finances to get cash at the lowest monthly payments. Whether it's debt consolidation or that home improvement you've been waiting for. On average we help identify savings of over $1,000 a year on average for Better users.
        </Trans>
      ),
    },
    [PARTNER.marble]: {
      default: isPuertoRico ? (
        <Trans>
          Our Debt Optimizer helps you understand your options in minutes. See your credit score and get clear information about your finances so you can decide on your next steps.
        </Trans>
      ) : (
        <Trans>
          Our Debt Optimizer helps you increase your homebuying power in minutes. Improve your credit score and optimize your finances to buy the home of your dreams. On average, Marble users experience an increase in home-buying power of nearly 30% and potential savings of over $1,000 per year.
        </Trans>
      ),
    },
    [PARTNER.renofi]: {
      default: isPuertoRico ? (
        <Trans>
          Is your credit or DTI (debt-to-income) affecting your options for a home improvement loan? Our Debt Optimizer gives you clear information about your credit and DTI so you can understand your options. Get your personalized results in minutes.
        </Trans>
      ) : (
        <Trans>
          Has your credit or DTI (debt-to-income) prevented you from qualifying for a home improvement loan? Our Debt Optimizer service gives you unbiased advice on how to fix your credit and improve your DTI so you can qualify for a second mortgage like you deserve. On average, we can increase score by 20 points in 2 months and identify over $100 in monthly savings. Get your personalized results in minutes.
        </Trans>
      ),
    },
    DEFAULT: isPuertoRico ? (
      <Trans>
        Our Debt Optimizer helps you understand your options in minutes. See your credit score and get clear information about your finances so you can decide on your next steps.
      </Trans>
    ) : (
      <Trans>
        Our Debt Optimizer helps you increase your homebuying power in minutes. Improve your credit score and optimize your finances to buy the home of your dreams. On average, users experience an increase in home-buying power of nearly 30% and potential savings of over $1,000 per year.
      </Trans>
    ),
  };

  const PARTNER_CTA: Record<string, Record<string, string>> & { DEFAULT: string } = {
    [PARTNER.renofi]: {
      default: isPuertoRico ? t`See My Options` : t`Qualify for a Loan Today`,
    },
    [PARTNER.better]: {
      default: isPuertoRico
        ? t`See My Options`
        : t`Increase My Homebuying Power`,
      cash: t`Get Cash`,
    },
    DEFAULT: isPuertoRico ? t`See My Options` : t`Increase My Homebuying Power`,
  };

  return (
    <div
      className={className}
      style={{ paddingTop: isMobile ? '25px' : '12px', overflowX: 'visible' }}
    >
      <TopOfFold
        alt={t`A magnifying glass searching for a home`}
        partnerLogo={{
          svg: logo,
          alt: t`${PARTNER_NAME[partner]} Logo`,
        }}
        copy={{
          h1Copy:
            PARTNER_HEADERS[partner]?.[campaign] || PARTNER_HEADERS.DEFAULT,
          pCopy: PARTNER_COPY[partner]?.[campaign] || PARTNER_COPY.DEFAULT,
        }}
        ctas={[
          {
            bgColor: PARTNER_COLOR[partner] || theme.green4,
            callback: onSignUp,
            cta: PARTNER_CTA[partner]?.[campaign] || PARTNER_CTA.DEFAULT,
            testid: 'saving-button',
          },
        ]}
        ImageOverride={
          isMobile ? (
            <House
              src={HouseTag}
              style={{
                transform: 'rotate(180)',
                position: 'absolute',
                right: '-64px',
                top: '48px',
                opacity: 0.25,
              }}
              alt={t`a small house with a red roof and a dollar sign tag attached`}
            />
          ) : (
            <House
              src={HouseTag}
              alt={t`a small house with a red roof and a dollar sign tag attached`}
            />
          )
        }
        imageLocation={{ desktop: 'left', mobile: 'top' }}
        copyWidth="100%"
      />
      <Spacer />
      <Spacer />
      <ColorBanner
        bannerColor="#F2994A"
        copy={{
          headerCopy: (
            <Trans>
              Lower your DTI <br />
              <span>(debt-to-income ratio)</span>
            </Trans>
          ),
          pCopy: (
            <Trans>
              Your DTI limits the mortgage you can take and is the number one reason for mortgage denial. The Debt Optimizer shows strategies to decrease your DTI that you can take action immediately.
            </Trans>
          ),
        }}
        imageMeta={{
          src: person1,
          mobilePosition: 'bottom',
          alt: isPuertoRico
            ? t`A borrower living her stress-free life after using Solve Finance`
            : t`A borrower living her stress-free life after Solve Finance fixes credit for her`,
        }}
      />
      <Spacer />
      <Spacer />
      <ImageAndList
        title={t`How it works`}
        image={
          <img
            src={LaptopWithMug}
            alt={t`A computer chugging away to optimize debts and scan all your options for you`}
          />
        }
        ctas={[]}
        listItems={howItWorksItems}
      />
      <Spacer />
      {!isMobile && (
        <ButtonLink
          onClick={onSignUp}
          bgColor={theme.green4}
          color={theme.white}
          style={{ minWidth: 227, margin: 'auto' }}
        >
          <Trans>Get Started</Trans>
        </ButtonLink>
      )}
      <div>
        <SectionTitle>
          {isPuertoRico ? (
            <Trans>Understand your borrowing options with less stress</Trans>
          ) : (
            <Trans>Get into a mortgage with less stress</Trans>
          )}
        </SectionTitle>
        <Paragraph>
          {isPuertoRico ? (
            <Trans>
              Within minutes the Debt Optimizer shows personalized, data-driven
              information about your DTI, your credit, and the options available
              to you.
            </Trans>
          ) : (
            <Trans>
              Within minutes the Debt Optimizer shows personalized data-driven
              options to lower DTI, increase credit score, and maximize the mortgage
              you qualify for.
            </Trans>
          )}
        </Paragraph>
      </div>
      <Spacer />
      {!isMobile && (
        <>
          <StyledImageAndList
            title={t`The Solve Difference`}
            image={
              <img
                src={CoffeeAndPencil}
                alt={
                  isPuertoRico
                    ? t`A coffee cup after you sit back and relax while Solve helps you review your borrowing options`
                    : t`A coffee cup after you sit back and relax letting Solve do the work on optimizing borrowing, fixing credit, and qualifying you for savings`
                }
              />
            }
            ctas={[
              {
                cta: t`Get started`,
                callback: onSignUp,
                color: theme.white,
                bgColor: theme.red4,
              },
            ]}
            listItems={solveDifferenceListItems}
            showGrid
          />
          <Spacer />
        </>
      )}

      <IconFocusCTA
        icon={<img src={BalloonDog} alt={t`balloon animal dog`} />}
        title={{
          text: isMobile
            ? null
            : isPuertoRico
              ? t`Get started with your debt today`
              : t`Start saving on your debt today`,
          size: 'l',
        }}
        paragraph={
          isMobile
            ? null
            : t`Try the Debt Optimizer to explore your best-available options. No strings attached`
        }
        ctas={[
          {
            text: t`Get Started`,
            callback: onSignUp,
            bgColor: isMobile ? '#EB5757' : theme.blue4,
            color: theme.white,
          },
        ]}
      />
      <Spacer />
      <UnbiasedResources isMobile={isMobile} />
      <Spacer />
      <Spacer />
    </div>
  );
};

export const PartnerLanding = styled(Landing)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: auto;
  max-width: 1200px;
  box-sizing: border-box;
  overflow-x: hidden;
`;

export default PartnerLanding;
